import { combineClass, type JSX } from 'cdui-js';
import { IOS_TAP_SLOP_PX } from '../../../lib/iosTapSlop';
import type { DiFiCheckBoxProps } from './types';

export const resolveLabel = (
  itemProps: DiFiCheckBoxProps,
  labelClass: string,
  wrapChildren: boolean,
): JSX.Element | null => {
  if (typeof itemProps.labelRender === 'function') {
    const node = itemProps.labelRender();
    return wrapChildren ? <span class={labelClass}>{node}</span> : node;
  }
  if (itemProps.label != null && itemProps.label !== '') {
    return <span class={labelClass}>{itemProps.label}</span>;
  }
  if (itemProps.children) {
    return wrapChildren ? <span class={labelClass}>{itemProps.children}</span> : itemProps.children;
  }
  return null;
};

const isNavOnlyTarget = (target: EventTarget | null) =>
  !!(target as Element | null)?.closest?.('[data-nav-only]');

/**
 * 触摸后抑制紧随其后的合成 click，避免 App WebView 双次切换。
 * 模块级即可：同一次手势只会打到一个控件。
 */
let lastTouchActivateAt = 0;
const TOUCH_CLICK_GUARD_MS = 450;

const activateFromEvent = (itemProps: DiFiCheckBoxProps, e: Event) => {
  if (itemProps.disabled === true) return;
  if (isNavOnlyTarget(e.target)) return;
  itemProps.onChange?.(itemProps.value, e as unknown as MouseEvent);
};

export const bindActivate = (itemProps: DiFiCheckBoxProps) => ({
  onclick: (e: MouseEvent) => {
    if (Date.now() - lastTouchActivateAt < TOUCH_CLICK_GUARD_MS) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    activateFromEvent(itemProps, e);
  },
  /**
   * WebView 滚动场景下 click 常被吞；touchend 兜底。
   * 轻微抖动（容差内）仍视为轻点；超出才当滚动，不切换。
   */
  ontouchend: (e: TouchEvent) => {
    if (itemProps.disabled === true) return;
    if (isNavOnlyTarget(e.target)) return;
    const t = e.changedTouches?.[0];
    const start = (e.currentTarget as HTMLElement | null)?.dataset;
    if (t && start?.touchX != null && start?.touchY != null) {
      const dx = Math.abs(t.clientX - Number(start.touchX));
      const dy = Math.abs(t.clientY - Number(start.touchY));
      if (dx > IOS_TAP_SLOP_PX || dy > IOS_TAP_SLOP_PX) return;
    }
    lastTouchActivateAt = Date.now();
    // 阻止随后 ghost click，避免再进 onclick 翻两次
    e.preventDefault();
    activateFromEvent(itemProps, e);
  },
  ontouchstart: (e: TouchEvent) => {
    const t = e.changedTouches?.[0] ?? e.touches?.[0];
    const el = e.currentTarget as HTMLElement | null;
    if (!t || !el) return;
    el.dataset.touchX = String(t.clientX);
    el.dataset.touchY = String(t.clientY);
  },
  onkeydown: (e: KeyboardEvent) => {
    if (itemProps.disabled === true) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      itemProps.onChange?.(itemProps.value, e as unknown as MouseEvent);
    }
  },
});

export const rootA11y = (itemProps: DiFiCheckBoxProps) => ({
  role: 'checkbox' as const,
  'aria-checked': itemProps.checked === true,
  'aria-disabled': itemProps.disabled === true,
  /**
   * WebView 下 tabindex=0 常导致「第一次只聚焦、不触发 click」。
   * 移动端以触摸为主，用 -1；键盘仍可通过父级/读屏激活时走 click。
   */
  tabindex: -1,
  'data-value':
    itemProps.value !== undefined && itemProps.value !== null ? String(itemProps.value) : undefined,
});

export const disabledClass = (itemProps: DiFiCheckBoxProps, base: string) =>
  combineClass(base, itemProps.disabled === true && `${base}--disabled`, itemProps.class);
